import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import env from '../config/env';
import { getStoredSelectedCompanyId } from './userCompanies.shared';
import type { CallType, CreateCallRequest } from '../lib/createCallSchema';

/*
 * One route per kind of call, as the dashboard posts them; each needs
 * `calls.create`, so the sheet's own gate covers all three.
 */
const CALL_ROUTES: Record<CallType, string> = {
  new: '/calls/new-call',
  completed: '/calls/completed-call',
  scheduled: '/calls/scheduled-call',
};

export const useCreateCall = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callType, ...call }: CreateCallRequest) => {
      // An office member with no Driver row has no driver company; the
      // stored selection stands in, as it does for every other request.
      const companyId = driver?.companyId || getStoredSelectedCompanyId();
      if (!companyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();

      const response = await fetch(`${env.apiUrl}${CALL_ROUTES[callType]}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-company-id': companyId,
        },
        body: JSON.stringify({
          ...call,
          vehicleType: 'LIGHT_DUTY', // Default vehicle type for driver app
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Keep the status and the body in the shape planRefusalOf reads, so
        // a call over the plan's monthly limit can say which plan allows more.
        throw Object.assign(
          new Error(
            errorData.message || `Failed to create call: ${response.statusText}`
          ),
          {
            status: response.status,
            response: { status: response.status, data: errorData },
          }
        );
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate both driver calls and company dispatches caches
      queryClient.invalidateQueries({ queryKey: ['driver-calls'] });
      queryClient.invalidateQueries({ queryKey: ['company-dispatches'] });
      // The new shell's calls list and Home's numbers, which the member who
      // created the call goes back to and expects to see it in.
      queryClient.invalidateQueries({ queryKey: ['mobile-calls'] });
      queryClient.invalidateQueries({ queryKey: ['company-stats'] });
    },
    onError: (error) => {
      console.error('Failed to create call:', error);
    },
  });
};
